import { useState } from "react";
import { CircleHelp, Link2, Plus } from "lucide-react";
import type {
  ClaimResponse,
  ClaimStrategyResponse,
} from "@patent-studio/api-types";
import { HoverTip } from "@/components/ui/HoverTip";
import { Menu, MenuGroup, MenuItem, MenuSeparator } from "@/components/ui/Menu";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Note } from "@/components/ui/Note";
import { Spinner } from "@/components/ui/Spinner";
import { StatusPill } from "@/components/ui/StatusPill";
import { useMoveClaim } from "@/hooks/use-claim-strategy";
import { refusalDetail } from "@/services/api-client";
import { ClaimCategoriesDialog } from "./ClaimCategoriesDialog";
import { NewCategoryDialog } from "./NewCategoryDialog";

const UNREACHED = "the change did not reach the server";

/** The numbers of every claim beneath one, however deep, in claim order. */
function beneathNumbers(
  claimId: string,
  claims: readonly ClaimResponse[],
): number[] {
  const found: number[] = [];
  const walk = (parent: string) => {
    for (const claim of claims) {
      if (claim.parent_claim_id === parent) {
        found.push(claim.number);
        walk(claim.claim_id);
      }
    }
  };
  walk(claimId);
  return found.sort((a, b) => a - b);
}

/** "Claim 2 takes the new category too.", or none for a claim with no dependents. */
function dependentsLine(numbers: readonly number[]): string | undefined {
  if (numbers.length === 0) return undefined;
  const verb = numbers.length === 1 ? "takes" : "take";
  return `Claim ${numbers.join(", ")} ${verb} the new category too.`;
}

/**
 * The pill's name for a reader, and how it is drawn: the category and who set
 * it, that Praxis is tagging, or, for a claim nothing has tagged, a call to
 * choose one. Each name starts with the pill's visible words, so a reader who
 * says what they see reaches it.
 */
function triggerOf(name: string, by: string | null, pending: boolean) {
  if (pending) {
    return {
      label:
        "Tagging…, claim category being chosen by Praxis. Choose a category",
      trigger: {
        content: (
          <>
            <Spinner size="sm" />
            Tagging…
          </>
        ),
        pill: "neutral" as const,
      },
    };
  }
  if (by === null) {
    return {
      label: "Choose a category, claim category not set",
      trigger: { content: "Choose a category", pill: "warning" as const },
    };
  }
  return {
    label: `${name}, claim category ${by}. Change category`,
    trigger: {
      content: name,
      pill: "brand" as const,
      tip: `Claim category · ${by}. Click to change.`,
    },
  };
}

/** Every category to retag to, with a mark on the current one, then a new category and what they mean. */
function CategoryMenu({
  claim,
  stage,
  pending,
  onRetag,
  onCreate,
  onExplain,
}: {
  claim: ClaimResponse;
  stage: ClaimStrategyResponse;
  pending: boolean;
  onRetag: (formId: string) => void;
  onCreate: () => void;
  onExplain: () => void;
}) {
  const category = stage.forms.find((form) => form.form_id === claim.form_id);
  const by =
    claim.form_origin === "placeholder"
      ? null
      : claim.form_origin === "attorney"
        ? "set by you"
        : "assigned by Praxis";
  const { label, trigger } = triggerOf(
    category?.name ?? "No category",
    by,
    pending,
  );
  return (
    <Menu name={label} trigger={trigger} align="start">
      <MenuGroup
        heading="Claim category"
        description={dependentsLine(
          beneathNumbers(claim.claim_id, stage.claims),
        )}
      >
        {stage.forms.map((form) => (
          <MenuItem
            key={form.form_id}
            title={form.name}
            checked={form.form_id === claim.form_id}
            end={
              form.origin === "attorney" ? (
                <MicroLabel size="sm">Custom</MicroLabel>
              ) : undefined
            }
            onSelect={() => {
              onRetag(form.form_id);
            }}
          />
        ))}
      </MenuGroup>
      <MenuSeparator />
      <MenuItem
        title="New claim category"
        brand
        icon={<Plus aria-hidden />}
        onSelect={onCreate}
      />
      <MenuItem
        title="What the categories mean"
        icon={<CircleHelp aria-hidden />}
        onSelect={onExplain}
      />
    </Menu>
  );
}

/**
 * An independent claim's category, as a pill that is the menu changing it: the
 * category with who set it, every category to retag to (the claim's
 * dependents follow), a new category made in the same write, and what the
 * categories mean. While Praxis tags the claim from its words, the pill says
 * so and still lets the attorney choose.
 */
export function CategoryTag({
  taskId,
  claim,
  stage,
  pending,
}: {
  taskId: string;
  claim: ClaimResponse;
  stage: ClaimStrategyResponse;
  /** Whether Praxis is tagging the claim from its words right now. */
  pending: boolean;
}) {
  const move = useMoveClaim(taskId);
  const [creating, setCreating] = useState(false);
  const [explaining, setExplaining] = useState(false);
  return (
    <>
      <CategoryMenu
        claim={claim}
        stage={stage}
        pending={pending}
        onRetag={(formId) => {
          move.mutate({ claimId: claim.claim_id, body: { form_id: formId } });
        }}
        onCreate={() => {
          setCreating(true);
        }}
        onExplain={() => {
          setExplaining(true);
        }}
      />
      {move.isError && !creating && (
        <Note tone="destructive" role="alert" className="mt-1 basis-full">
          {`The category could not be changed: ${refusalDetail(move.error, UNREACHED)}`}
        </Note>
      )}
      <NewCategoryDialog
        open={creating}
        pending={move.isPending}
        error={creating ? move.error : null}
        onCreate={(made) => {
          move.mutate(
            { claimId: claim.claim_id, body: { new_category: made } },
            {
              onSuccess: () => {
                setCreating(false);
              },
            },
          );
        }}
        onClose={() => {
          setCreating(false);
          move.reset();
        }}
      />
      <ClaimCategoriesDialog
        open={explaining}
        onClose={() => {
          setExplaining(false);
        }}
      />
    </>
  );
}

/** A dependent claim's category, inherited from its independent claim and changed only there. */
export function InheritedTag({
  claim,
  stage,
}: {
  claim: ClaimResponse;
  stage: ClaimStrategyResponse;
}) {
  const category = stage.forms.find((form) => form.form_id === claim.form_id);
  const root = rootOf(claim, stage.claims);
  const tip = `Inherited from claim ${String(root.number)}. Change the category on claim ${String(root.number)} to retag it.`;
  return (
    <HoverTip tip={tip} tone="dark">
      {({ triggerProps }) => (
        <StatusPill
          tone="neutral"
          size="sm"
          plain
          icon={<Link2 aria-hidden className="size-3" />}
          tabIndex={0}
          aria-label={`Claim category: ${category?.name ?? "No category"}, inherited from claim ${String(root.number)}`}
          {...triggerProps}
        >
          {category?.name ?? "No category"}
        </StatusPill>
      )}
    </HoverTip>
  );
}

/** The independent claim a chain hangs from. */
function rootOf(claim: ClaimResponse, claims: readonly ClaimResponse[]) {
  let at = claim;
  while (at.parent_claim_id !== null) {
    const parent = claims.find((c) => c.claim_id === at.parent_claim_id);
    if (parent === undefined) break;
    at = parent;
  }
  return at;
}
